import { useState, useEffect } from 'react';
import { useApp } from '@/components/AppLayout';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { CreditCard, Check, AlertCircle, ExternalLink, Loader2, ArrowLeft, RefreshCw, Building2 } from 'lucide-react';
import { stripeApi, businessApi } from '@/lib/api';

function StripeConnectSection() {
  const { currentBusiness, featureFlags, refreshFeatureFlags } = useApp();
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const handleConnect = async () => {
    if (!currentBusiness) return;

    setError('');
    setConnecting(true);

    const response = await stripeApi.connect(currentBusiness.id);

    if (response.error) {
      setError(response.error);
      setConnecting(false);
      return;
    }

    if (response.data?.onboarding_url) {
      window.location.href = response.data.onboarding_url;
    }
  };

  const handleOpenDashboard = () => {
    window.open('https://dashboard.stripe.com', '_blank');
  };

  const handleRefresh = async () => {
    if (!currentBusiness) return;

    setError('');
    setRefreshing(true);

    const response = await stripeApi.refresh(currentBusiness.id);

    setRefreshing(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    // Refresh the shared feature flags in context (updates both this page and the banner)
    refreshFeatureFlags();
  };

  if (!currentBusiness) return null;

  // Show loading state if feature flags haven't loaded yet
  if (!featureFlags) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Stripe Connect
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Separator className="mb-6" />
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const isConnected = featureFlags.payments_setup_complete;
  const isPartiallyConnected = featureFlags.stripe_account_id && !featureFlags.payments_setup_complete;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          Stripe Connect
        </CardTitle>
        <CardDescription>
          Connect your Stripe account to accept payments.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Separator className="mb-6" />

        {error && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
            <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
          </div>
        )}

        {isConnected ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Stripe Connected</p>
                <p className="text-sm text-muted-foreground">
                  Your account is set up to accept payments.
                </p>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span>Card payments enabled</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span>Bank transfers enabled</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span>Payouts to your bank account</span>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={handleOpenDashboard}
              disabled={connecting}
            >
              {connecting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2 h-4 w-4" />
              )}
              Open Stripe Dashboard
            </Button>
          </div>
        ) : isPartiallyConnected ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Setup Incomplete</p>
                <p className="text-sm text-muted-foreground">
                  Finish setting up Stripe to start accepting payments.
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleConnect}
                disabled={connecting || refreshing}
                className="bg-[#635bff] hover:bg-[#5851ea] text-white"
              >
                {connecting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Continue Setup
              </Button>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={connecting || refreshing}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span className="sr-only">Refresh Status</span>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Already completed setup? Click refresh to sync status.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm">
                Connect with Stripe to accept credit card and bank transfer payments directly on your invoices.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Get paid faster with online payments</li>
                <li>• Accept all major credit cards</li>
                <li>• Automatic payment tracking</li>
                <li>• Secure, PCI-compliant processing</li>
              </ul>
            </div>

            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-[#635bff] hover:bg-[#5851ea] text-white"
            >
              {connecting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="mr-2 h-4 w-4" />
              )}
              Connect with Stripe
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DefaultPaymentMethodsSection() {
  const { currentBusiness, featureFlags } = useApp();
  const [cardEnabled, setCardEnabled] = useState(true);
  const [achEnabled, setAchEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Initialize from current business when it loads
  useEffect(() => {
    if (currentBusiness) {
      setCardEnabled(currentBusiness.default_stripe_card_enabled ?? true);
      setAchEnabled(currentBusiness.default_stripe_ach_enabled ?? false);
    }
  }, [currentBusiness]);

  // Only show if Stripe is fully connected
  if (!featureFlags?.payments_setup_complete) return null;

  const handleSave = async (field: 'card' | 'ach', value: boolean) => {
    if (!currentBusiness) return;

    setError('');
    setSaving(true);

    const updates: { default_stripe_card_enabled?: boolean; default_stripe_ach_enabled?: boolean } = {};
    if (field === 'card') {
      updates.default_stripe_card_enabled = value;
    } else {
      updates.default_stripe_ach_enabled = value;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await businessApi.update(currentBusiness.id, updates as any);

    setSaving(false);

    if (response.error) {
      setError(response.error);
      // Revert the toggle
      if (field === 'card') setCardEnabled(!value);
      else setAchEnabled(!value);
      return;
    }
    // Local state is already updated, no need to refresh
  };

  const handleCardChange = (checked: boolean) => {
    setCardEnabled(checked);
    handleSave('card', checked);
  };

  const handleAchChange = (checked: boolean) => {
    setAchEnabled(checked);
    handleSave('ach', checked);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Default Payment Methods
        </CardTitle>
        <CardDescription>
          Choose which payment methods are enabled by default for new invoices and quotes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Separator className="mb-6" />

        {error && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
            <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="card-toggle" className="text-sm font-medium">Credit/Debit Card</Label>
              <p className="text-sm text-muted-foreground">Accept Visa, Mastercard, American Express</p>
            </div>
            <Switch
              id="card-toggle"
              checked={cardEnabled}
              onCheckedChange={handleCardChange}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="ach-toggle" className="text-sm font-medium">ACH Bank Transfer</Label>
              <p className="text-sm text-muted-foreground">Accept direct bank transfers (lower fees)</p>
            </div>
            <Switch
              id="ach-toggle"
              checked={achEnabled}
              onCheckedChange={handleAchChange}
              disabled={saving}
            />
          </div>
        </div>

        {saving && (
          <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentProcessingContent() {
  const { nav } = useBusinessNav();

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="space-y-1 mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2 text-muted-foreground"
          onClick={() => nav('/settings')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Settings
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Payment Processing</h1>
        <p className="text-sm text-muted-foreground">
          Accept online payments directly from your invoices.
        </p>
      </div>

      <div className="space-y-6">
        <StripeConnectSection />
        <DefaultPaymentMethodsSection />
      </div>
    </div>
  );
}

export function PaymentProcessing() {
  return <PaymentProcessingContent />;
}
